import styles from "./retirement.module.css"
import React from "react"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import socialSecurity from "shared/social-security"
import BarChart from "activities/components/charts/bar"
import assign from "object-assign"
import AccessibilityTable from "activities/components/shared/accessibility-table"

// delayed retirement chart: https://www.ssa.gov/benefits/retirement/planner/1960-delay.html
// early retirement chart: https://www.ssa.gov/benefits/retirement/planner/1960.html

// updated via the links above, as of Feb 2024
// the numbers at that link stay the same as of May 2025
const ageAdjustmentIndex = {
  62: 0.7,
  63: 0.75,
  64: 0.8,
  65: 0.867,
  66: 0.933,
  67: 1,
  68: 1.08,
  69: 1.16,
  70: 1.24
}

const utils = ({currentSalary, currentSavings, monthlySavingsRate, currentAge, retirementAge, lifeExpectancy, interestRate, percentToLiveOn}) => {
  const decimalSavingsRate = monthlySavingsRate / 100
  const decimalRate = interestRate / 100
  const decimalLiveOn = percentToLiveOn / 100

  const monthlyBenefit = retirementAge < 62 ? 0 : socialSecurity(currentSalary) * ageAdjustmentIndex[retirementAge]
  const numberOfRetirementMonths = (lifeExpectancy - retirementAge + 1) * 12
  const monthlyInterestRate = decimalRate / 12
  const yearsArrayForChart = Array.from(Array(lifeExpectancy - currentAge + 1), (x, i) => i + currentAge)
  const monthlyPayment = (currentSalary / 12) * decimalSavingsRate
  const desiredMonthlyRetirementIncome = (currentSalary * decimalLiveOn) / 12
  const desiredMonthlyRetirementIncomeFromSavings = desiredMonthlyRetirementIncome - monthlyBenefit

  const amountSavedWithPayments = (years) => monthlyPayment * ((Math.pow(1 + monthlyInterestRate, 12 * years) - 1) / monthlyInterestRate) * (1 + monthlyInterestRate)
  const amountSavedWithPrincipal = (years) => currentSavings * Math.pow(1 + monthlyInterestRate, 12 * years)
  const preRetirementSavingsByYear = (years) => amountSavedWithPrincipal(years) + amountSavedWithPayments(years)
  const totalActualRetirementSavings = preRetirementSavingsByYear(retirementAge - currentAge - 1)
  const actualMonthlyRetirementIncomeFromSavings =
    (totalActualRetirementSavings * (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfRetirementMonths))) /
    (Math.pow(1 + monthlyInterestRate, numberOfRetirementMonths) - 1)
  const actualMonthlyRetirementIncome = actualMonthlyRetirementIncomeFromSavings + monthlyBenefit

  // this is calculated based on the amount we want to be pulling per month during retirement, which is based on
  // percentToLiveOn and currentSalary and then social security is subtracted off that, and we use it below
  const presentValueOfAnnuityDue =
    (desiredMonthlyRetirementIncomeFromSavings + desiredMonthlyRetirementIncomeFromSavings) *
    ((1 - Math.pow(1 + monthlyInterestRate, -(numberOfRetirementMonths - 1))) / monthlyInterestRate)

  const futureValueOfAnnuityDue = (months) =>
    Array.from(Array(months).keys()).reduce((memo) => (memo - desiredMonthlyRetirementIncomeFromSavings) * (1 + monthlyInterestRate), totalActualRetirementSavings)

  const retirementSavingsDataForRecharts = yearsArrayForChart.map((age, i) => ({
    year: age,
    currentSavings: age < retirementAge - 1 ? preRetirementSavingsByYear(i) : futureValueOfAnnuityDue((age - retirementAge + 1) * 12)
  }))

  const retirementCalculatorData = {
    legend: {
      x: {label: "Age", key: "year"},
      y: {
        label: "Savings",
        type: "currency",
        changeBarColorOnNegativeNum: true,
        barToChangeOnNegative: "currentSavings"
      }
    },
    fields: {
      year: {type: "number", tooltip: {title: "Age", order: 0}},
      currentSavings: {
        type: "currency",
        barOrder: 0,
        tooltip: {title: "Savings", order: 1, color: "color-1"}
      }
    },
    values: {
      x: yearsArrayForChart,
      y: retirementSavingsDataForRecharts
    }
  }

  return {
    actualMonthlyRetirementIncome,
    totalActualRetirementSavings,
    presentValueOfAnnuityDue,
    monthlyBenefit,
    retirementCalculatorData
  }
}

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const state = getState()
    const {currentSalary, currentSavings, monthlySavingsRate, currentAge, retirementAge, interestRate, percentToLiveOn, resetKey} = state
    const {theme, id} = getProps()
    const {actualMonthlyRetirementIncome, totalActualRetirementSavings, presentValueOfAnnuityDue, monthlyBenefit, retirementCalculatorData} = utils(state)
    const retirementGoalTooltipText = "Percentage of your annual income you hope to live on during retirement."
    const monthlySavingsTooltipText = "Percentage of your income you plan on saving, monthly, until retirement."
    const rateOfReturnTooltipText = "Percent return you expect on your savings."
    const currentAgeTooltipText = "How old are you now?"
    const retireAtTooltipText = "Age at which you expect to retire."
    const estimatedIncomeTooltipText = `Estimates average monthly distribution from your retirement fund and includes a projected ${util.formatDollars(
      monthlyBenefit
    )} from Social Security.`
    const retirementFundTooltipText = `Estimates the total amount you will have saved by retirement. Does not include an estimated ${util.formatDollars(
      monthlyBenefit
    )} monthly from Social Security.`
    const neededToRetireTooltipText = `Estimates the total savings you will need to meet your goal by the time you reach retirement. Does not include an estimated ${util.formatDollars(
      monthlyBenefit
    )} monthly from Social Security.`

    const accessibilityTableHeadings = [
      {
        name: "Age",
        key: "year"
      },
      {
        name: "Savings",
        key: "currentSavings"
      }
    ]

    const accessibilityTableData = retirementCalculatorData.values.y.map((row, idx) => ({
      year: idx + 30,
      currentSavings: util.formatDollarsNoCents(row.currentSavings)
    }))

    const accessibilityTableLabel = "Retirement Savings Summary"

    return (
      <div>
        <shared.SummaryCalcContainer>
          <shared.InputsContainer className={styles["inputs"]}>
            <inputs.SmallCalcInputWithTitle
              id={id}
              includeIcon={false}
              title="Annual Pre-Tax Income"
              onChange={updateState("currentSalary", util.fromString)}
              initialValue={currentSalary}
              resetKey={resetKey}
            />
            <inputs.SmallCalcInputWithTitle
              id={id}
              includeIcon={false}
              title="Current Savings"
              onChange={updateState("currentSavings", util.fromString)}
              initialValue={currentSavings}
              resetKey={resetKey}
            />
            <inputs.RangeInput
              id={id}
              title="Retirement Goal"
              onChange={updateState("percentToLiveOn", parseFloat)}
              initialValue={percentToLiveOn}
              secondaryValue={currentSalary * (percentToLiveOn / 100)}
              min={30}
              max={100}
              step={5}
              units="%"
              valueType="percent-100"
              tooltipText={retirementGoalTooltipText}
              resetKey={resetKey}
            />
            <inputs.RangeInput
              id={id}
              title="Monthly Savings"
              onChange={updateState("monthlySavingsRate", parseFloat)}
              initialValue={monthlySavingsRate}
              min={1}
              max={50}
              step={0.5}
              units="%"
              valueType="percent"
              tooltipText={monthlySavingsTooltipText}
              resetKey={resetKey}
            />
            <inputs.RangeInput
              id={id}
              title="Rate of Return"
              onChange={updateState("interestRate", parseFloat)}
              initialValue={interestRate}
              min={0.5}
              max={10}
              step={0.25}
              units="%"
              valueType="percent"
              tooltipText={rateOfReturnTooltipText}
              resetKey={resetKey}
            />
            <inputs.RangeInput
              id={id}
              title="Current Age"
              onChange={updateState("currentAge", util.fromString)}
              initialValue={currentAge}
              min={16}
              max={65}
              step={1}
              units="years"
              valueType="years-full"
              tooltipText={currentAgeTooltipText}
              resetKey={resetKey}
            />
            <inputs.RangeInput
              id={id}
              title="Retirement Age"
              onChange={updateState("retirementAge", util.fromString)}
              initialValue={retirementAge}
              min={50}
              max={90}
              step={1}
              units="years"
              valueType="years-full"
              tooltipText={retireAtTooltipText}
              resetKey={resetKey}
            />
          </shared.InputsContainer>
          <shared.ChartContainer>
            <BarChart
              data={retirementCalculatorData}
              theme={theme}
              aria-label="This bar graph visually represents how much your retirement savings will grow over the years, based on your input. Read below to find your average monthly income after retirement, the actual total for your retirement fund, and the goal for your retirement fund."
            />
          </shared.ChartContainer>
          <shared.SummaryContainer>
            <callouts.Callout>
              <callouts.Detail
                label={"Estimated Income"}
                result={util.formatDollarsNoCents(actualMonthlyRetirementIncome)}
                tooltipText={estimatedIncomeTooltipText}
                dataTestId="estimated-income"
              />
              <callouts.Detail
                label="Actual Retirement Fund"
                result={util.formatDollarsNoCents(totalActualRetirementSavings)}
                tooltipText={retirementFundTooltipText}
                dataTestId="actual-fund"
              />
              <callouts.Detail
                label="Goal Retirement Fund"
                result={util.formatDollarsNoCents(presentValueOfAnnuityDue)}
                tooltipText={neededToRetireTooltipText}
                dataTestId="goal-fund"
              />
            </callouts.Callout>
          </shared.SummaryContainer>
        </shared.SummaryCalcContainer>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {actualMonthlyRetirementIncome, totalActualRetirementSavings, presentValueOfAnnuityDue} = utils(state)
      return assign(state, {
        estimatedIncome: util.round(actualMonthlyRetirementIncome),
        actualRetirementFund: util.round(totalActualRetirementSavings),
        goalRetirementFund: util.round(presentValueOfAnnuityDue)
      })
    },
    defaultProps: {
      id: "retirement-calculator",
      className: "retirement-calculator",
      title: "Retirement Calculator",
      subtitle: "See how much you should be saving in order to enjoy the standard of living you want when you retire."
    },
    defaultState: {
      currentSalary: 50000,
      currentSavings: 15000,
      monthlySavingsRate: 4,
      currentAge: 30,
      retirementAge: 67,
      lifeExpectancy: 100,
      interestRate: 4,
      percentToLiveOn: 75
    }
  }
)
